<template>
  <div
    :class="[$style.labelContainer, isPriceDropAlertActive ? $style.active : undefined]"
    @click="!isPriceDropAlertActive && handlePriceDropNotificationClicked()"
  >
    <HgIcon
      v-if="!isPriceDropAlertActive"
      icon="BellFilledRinging"
      :class="$style.icon"
      :color="iconColor"
    />
    <HgIcon v-else icon="Bell" :class="$style.icon" :color="iconColor" />
    <span
      :class="[
        $style.title,
        isPriceDropAlertActive
          ? isPlatformIs24()
            ? $style.titleActiveIs24
            : $style.titleActive
          : undefined,
        isPlatformIs24() ? $style.titleIs24 : undefined,
      ]"
      >{{ t(priceDropAlertsTranslationKey) }}</span
    >
  </div>
  <SavePriceDropModal
    :isModalVisible="isModalOpen"
    :listing="listing"
    @close="handleModalClose"
    @savePriceDropAlert="handleSavePriceDropAlert"
  />
</template>

<script lang="ts" setup>
import SavePriceDropModal from '@/components/SavePriceDropModal/SavePriceDropModal.vue';
import { isPlatformIs24 } from '@/helpers/platformDetection';
import { usePriceDropAlertStore } from '@/store/modules/price-drop-alert';
import { CreatePriceDropAlertEmitPayload } from '@/store/types/price-drop-alert-state';
import { HgIcon } from '@smg-real-estate/domus';
import { Listing } from '@smg-real-estate/hg-rets-schema';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { trackPriceDropNotificationClicked } from './tracking';

const priceDropAlertStore = usePriceDropAlertStore();
const { t } = useI18n();

defineOptions({
  name: 'PriceDropNotification',
});

interface Props {
  listing: Listing;
}
defineProps<Props>();

const emit = defineEmits<{
  savePriceDropAlert: [payload: CreatePriceDropAlertEmitPayload];
}>();

const isPriceDropAlertActive = computed((): boolean => {
  return priceDropAlertStore.isPriceDropAlertActive;
});
const priceDropAlertsTranslationKey = computed((): string => {
  return isPriceDropAlertActive.value
    ? 'priceDropNotifications.active'
    : 'priceDropNotifications.title';
});
const iconColor = computed((): string => {
  if (isPlatformIs24()) {
    return isPriceDropAlertActive.value
      ? 'var(--icon-active-color-is24)'
      : 'var(--icon-inactive-color-is24)';
  }
  return isPriceDropAlertActive.value ? 'var(--icon-active-color)' : 'var(--icon-inactive-color)';
});
const isModalOpen = computed((): boolean => {
  return priceDropAlertStore.getIsModalOpen;
});

const handlePriceDropNotificationClicked = () => {
  trackPriceDropNotificationClicked();
  priceDropAlertStore.setIsModalOpen(true);
};
const handleModalClose = () => {
  priceDropAlertStore.setIsModalOpen(false);
};
const handleSavePriceDropAlert = (payload: CreatePriceDropAlertEmitPayload) => {
  emit('savePriceDropAlert', payload);
};
</script>

<style module lang="scss">
@import '@/assets/variables';

.labelContainer {
  display: flex;
  gap: $space-50;
  align-items: center;
  cursor: pointer;

  --icon-inactive-color: #{$color-blue-60};
  --icon-active-color: #{$color-green-60};
  --icon-inactive-color-is24: #{$color-azul-110};
  --icon-active-color-is24: #{$color-teal-120};

  .icon {
    width: $space-150;
    height: $space-150;
  }

  .title {
    @include body-2-bold;
    color: $color-blue-60;
  }

  .titleActive {
    color: $color-green-60;
  }

  .titleIs24 {
    color: $color-azul-110;
  }

  .titleActiveIs24 {
    color: $color-teal-120;
  }
}

.labelContainer.active {
  cursor: default;
}
</style>
